import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncHandler } from "../../../hooks/useAsyncHandler";
import { useConfirmHandler } from "../../../hooks/useConfirmHandler";
import {
  getMissingSectionFileCounts,
  startDocumentSectionsRefresh,
  type MissingSectionFilesResponse,
} from "../../../lib/corpusApi";
import { ROUTES } from "../../../route_paths";
import {
  describeDocumentSectionsRefreshConfirmation,
  describeMissingSectionFilesBadge,
} from "./missing_section_files_scan_views";

/** Scans Storage on demand for missing section files, and starts the document sections refresh
 *  for the documents missing them, going to that job's page once it exists. */
export function useMissingSectionFilesScan() {
  const navigate = useNavigate();
  const [result, setResult] = useState<MissingSectionFilesResponse | null>(null);

  const scan = useAsyncHandler(async () => {
    setResult(await getMissingSectionFileCounts());
  });

  const { handle: confirmLaunch, dialogProps } = useConfirmHandler({
    ...(result
      ? describeDocumentSectionsRefreshConfirmation(result)
      : { title: "Refresh sections?", message: "" }),
    confirmLabel: "Start job",
  });

  function launch() {
    confirmLaunch(async () => {
      const job = await startDocumentSectionsRefresh({});
      navigate(ROUTES.internal.batchJobs.detail.path({ id: job.id }));
    });
  }

  const badge = result ? describeMissingSectionFilesBadge(result) : null;

  return { result, badge, scan, launch, dialogProps };
}

export type UseMissingSectionFilesScanResult = ReturnType<typeof useMissingSectionFilesScan>;
